import React, { useCallback, useEffect, useState, useRef, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, ChevronLeft, AlertTriangle, Pause } from 'lucide-react';
interface Episode2NavScreenProps {
  onBack: () => void;
}
export function Episode2NavScreen({
  onBack
}: Episode2NavScreenProps) {
  const [hasWatched, setHasWatched] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showPause, setShowPause] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const videoContainerRef = useRef<HTMLDivElement>(null);
  const handlePlayFullscreen = useCallback(async () => {
    const video = videoRef.current;
    const container = videoContainerRef.current;
    if (!video || !container) return;
    setIsPlaying(true);
    setShowPause(false);
    try {
      if (container.requestFullscreen) {
        await container.requestFullscreen();
      } else if ((container as any).webkitRequestFullscreen) {
        await (container as any).webkitRequestFullscreen();
      }
      await video.play();
    } catch (err) {
      await video.play();
    }
  }, []);
  const exitFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else if ((document as any).webkitFullscreenElement) {
      ;
      (document as any).webkitExitFullscreen();
    }
  }, []);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const handleEnded = () => {
      setIsPlaying(false);
      exitFullscreen();
      // Show pause overlay after video ends
      setTimeout(() => {
        setShowPause(true);
      }, 400);
    };
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement && !(document as any).webkitFullscreenElement) {
        if (video && video.ended) {
          setIsPlaying(false);
          setTimeout(() => {
            setShowPause(true);
          }, 400);
        }
      }
    };
    video.addEventListener('ended', handleEnded);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    return () => {
      video.removeEventListener('ended', handleEnded);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, [exitFullscreen]);
  const dismissPause = () => {
    setShowPause(false);
    setHasWatched(true);
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    },
    exit: {
      opacity: 0,
      y: -50,
      transition: {
        duration: 0.5
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: 'easeOut'
      }
    }
  };
  return <motion.div className="min-h-screen w-full bg-[var(--bg-secondary)] flex flex-col relative overflow-hidden" variants={containerVariants} initial="hidden" animate="visible" exit="exit" data-id="element-6">
      {/* Background Texture */}
      <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] pointer-events-none mix-blend-overlay" data-id="element-7" />

      {/* Fullscreen video container */}
      <div ref={videoContainerRef} className={`${isPlaying && !showPause ? 'block' : 'hidden'} fixed inset-0 z-50 bg-black flex items-center justify-center`} data-id="element-8">
        <video ref={videoRef} className="w-full h-full object-contain bg-black" controls controlsList="nodownload noplaybackrate" disablePictureInPicture onContextMenu={e => e.preventDefault()} playsInline preload="auto" data-id="element-9">
          <source src="https://cdn.shopify.com/videos/c/o/v/46f24469e7894a26a2d058257b630f2c.mp4" type="video/mp4" data-id="element-10" />
        </video>
      </div>

      {/* PAUSE OVERLAY */}
      <AnimatePresence data-id="element-11">
        {showPause && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 1
      }} className="fixed inset-0 z-50 bg-black/95 flex flex-col items-center justify-center px-6 text-center" data-id="element-12">
            {/* Subtle red line accent */}
            <motion.div initial={{
          scaleX: 0
        }} animate={{
          scaleX: 1
        }} transition={{
          delay: 0.5,
          duration: 0.8
        }} className="w-16 h-0.5 bg-[var(--accent)] mb-10 origin-center" data-id="element-13" />

            <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3,
          duration: 0.8
        }} className="flex items-center gap-3 mb-8" data-id="element-14">
              <Pause className="w-6 h-6 text-[var(--accent)]" data-id="element-15" />
              <h2 className="text-5xl md:text-7xl font-serif font-bold tracking-widest text-[var(--text-primary)]" data-id="element-16">
                PAUSA
              </h2>
            </motion.div>

            <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.7,
          duration: 0.8
        }} className="max-w-xl space-y-4 mb-12" data-id="element-17">
              <p className="text-xl md:text-2xl font-serif text-[var(--text-primary)] leading-relaxed italic" data-id="element-18">
                ¿Qué teoría explica
              </p>
              <p className="text-xl md:text-2xl font-serif text-[var(--accent)] leading-relaxed font-semibold" data-id="element-19">
                drogas + traslado + estrangulamiento?
              </p>
            </motion.div>

            <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 1.2,
          duration: 0.8
        }} className="text-[var(--text-secondary)] text-lg font-light tracking-wide mb-16" data-id="element-20">
              Escriban su hipótesis antes de continuar.
            </motion.p>

            <motion.button initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 1.8,
          duration: 0.6
        }} onClick={dismissPause} className="px-10 py-4 border border-[var(--accent)] text-[var(--text-primary)] uppercase tracking-[0.2em] text-sm transition-all duration-500 hover:bg-[var(--accent-glow)] hover:scale-105" data-id="element-21">
              Continuar
            </motion.button>

            {/* Bottom decorative line */}
            <motion.div initial={{
          scaleX: 0
        }} animate={{
          scaleX: 1
        }} transition={{
          delay: 2,
          duration: 0.8
        }} className="w-16 h-0.5 bg-[var(--accent)]/30 mt-10 origin-center" data-id="element-22" />
          </motion.div>}
      </AnimatePresence>

      {/* Navigation Bar */}
      <nav className="w-full p-6 flex justify-between items-center z-30" data-id="element-23">
        <button onClick={onBack} className="flex items-center text-[var(--text-secondary)] hover:text-white transition-colors text-sm tracking-widest uppercase group" data-id="element-24">
          <ChevronLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" data-id="element-25" />
          Volver
        </button>
      </nav>

      {/* Main Content */}
      <div className="flex-1 flex flex-col justify-center items-center px-6 md:px-12 z-20" data-id="element-26">
        <div className="max-w-4xl w-full mx-auto" data-id="element-27">
          <motion.div variants={itemVariants} className="mb-4" data-id="element-28">
            <span className="text-[var(--text-secondary)] uppercase tracking-[0.2em] text-xs md:text-sm font-medium border-l-2 border-[var(--accent)] pl-4" data-id="element-29">
              Temporada 1 — Caso del Auto Blanco
            </span>
          </motion.div>

          <motion.h2 variants={itemVariants} className="text-4xl md:text-6xl lg:text-7xl font-serif font-bold text-[var(--text-primary)] mb-8 leading-tight" data-id="element-30">
            EPISODIO 2 — <br className="md:hidden" data-id="element-31" />
            <span className="text-white" data-id="element-32">AUTOPSIA</span>
          </motion.h2>

          <motion.div variants={itemVariants} className="w-24 h-1 bg-[var(--accent)] mb-10" data-id="element-33" />

          <motion.div variants={itemVariants} className="space-y-4 mb-12 max-w-2xl" data-id="element-34">
            <p className="text-lg md:text-xl font-light text-[var(--text-primary)] leading-relaxed" data-id="element-35">
              Cinco días después, el cuerpo aparece.
            </p>
            <p className="text-lg md:text-xl font-light text-[var(--text-primary)] leading-relaxed" data-id="element-36">
              La desaparición se convierte en homicidio.
            </p>
            <p className="text-lg md:text-xl font-light text-[var(--text-primary)] leading-relaxed" data-id="element-37">
              Y las pruebas cambian todo.
            </p>
          </motion.div>

          {/* Discovery section */}
          <motion.div variants={itemVariants} className="mb-12 max-w-2xl" data-id="element-38">
            <h3 className="text-lg font-serif text-[var(--text-secondary)] uppercase tracking-widest mb-6" data-id="element-39">
              ¿Qué vas a descubrir en este episodio?
            </h3>
            <ul className="space-y-3 text-[var(--text-primary)] font-light" data-id="element-40">
              <li className="flex items-center" data-id="element-41">
                <span className="w-1.5 h-1.5 bg-[var(--accent)] rounded-full mr-3 flex-shrink-0" data-id="element-42" />
                La causa real de la muerte
              </li>
              <li className="flex items-center" data-id="element-43">
                <span className="w-1.5 h-1.5 bg-[var(--accent)] rounded-full mr-3 flex-shrink-0" data-id="element-44" />
                El hallazgo de alcohol y alprazolam
              </li>
              <li className="flex items-center" data-id="element-45">
                <span className="w-1.5 h-1.5 bg-[var(--accent)] rounded-full mr-3 flex-shrink-0" data-id="element-46" />
                La pregunta clave: ¿cómo llegó hasta Guadarrama?
              </li>
            </ul>
          </motion.div>

          <motion.div variants={itemVariants} className="mb-12" data-id="element-47">
            <button onClick={handlePlayFullscreen} className="flex items-center justify-center px-8 py-4 bg-[var(--accent)] text-white font-medium tracking-widest uppercase hover:bg-[var(--accent-hover)] transition-all duration-300 shadow-[0_0_30px_rgba(139,26,26,0.3)] hover:shadow-[0_0_50px_rgba(139,26,26,0.5)] hover:scale-105" data-id="element-48">
              <Play className="w-5 h-5 mr-3 fill-current" data-id="element-49" />
              {hasWatched ? 'Volver a Ver' : 'Ver Episodio 2'}
            </button>
          </motion.div>

          {/* Post-video content revealed after watching + dismissing pause */}
          <AnimatePresence data-id="element-50">
            {hasWatched && <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            ease: 'easeOut'
          }} className="space-y-12 max-w-3xl" data-id="element-51">
                <div className="w-full h-px bg-white/10" data-id="element-52" />

                {/* Warning Box */}
                <motion.div className="bg-[var(--bg-card)] border-l-4 border-[var(--accent)] p-6 md:p-8 relative overflow-hidden" initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.3
            }} data-id="element-53">
                  <div className="absolute top-0 right-0 p-4 opacity-5" data-id="element-54">
                    <AlertTriangle className="w-24 h-24" data-id="element-55" />
                  </div>
                  <div className="relative z-10" data-id="element-56">
                    <div className="flex items-center mb-4 text-[var(--accent)]" data-id="element-57">
                      <AlertTriangle className="w-5 h-5 mr-2" data-id="element-58" />
                      <span className="font-bold tracking-widest uppercase text-sm" data-id="element-59">
                        Importante
                      </span>
                    </div>
                    <p className="text-[var(--text-primary)] text-lg mb-2" data-id="element-60">
                      Este episodio incluye una pausa obligatoria.
                    </p>
                    <p className="text-[var(--text-secondary)]" data-id="element-61">
                      No usen el celular.
                    </p>
                  </div>
                </motion.div>

                {/* Next Episode Button */}
                <motion.div className="flex justify-center pt-4 pb-12" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: 0.6
            }} data-id="element-62">
                  <button className="px-8 py-4 border border-white/10 text-[var(--text-secondary)] uppercase tracking-widest text-sm hover:bg-white/5 hover:text-white hover:border-white/30 transition-all duration-300" data-id="element-63">
                    Continuar al Episodio 3
                  </button>
                </motion.div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>;
}